/**
 * ISMS Journey Admin Editor
 *
 * Accessible only to tenant 001 (Mercell super-admin).
 * Edits the global ISMS journey flow config that is served to all tenants.
 */

import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { api, getAuthHeaders } from '../services/api';
import { API_BASE_URL } from '../config';
import { useAuth } from '../hooks/useAuth';
import { useSwal } from '../hooks/useSwal';
import { isPlatformAdmin } from '../utils/adminAccess';
import { PageHeader } from '../components/PageHeader';
import { NimbusModal, NimbusModalContent } from '../components/NimbusModal';
import {
  FLOW_COLUMNS,
  FLOW_PHASES,
} from '../data/ismsFlowData';
import type { FlowStep, FlowColumn, FlowPhase } from '../data/ismsFlowData';
import ReactQuill from 'react-quill';
import 'react-quill/dist/quill.snow.css';

// ─── Types ───────────────────────────────────────────────────────────────────

interface DraftState { phases: FlowPhase[]; columns: FlowColumn[]; globalStepOrder: string[]; }
type SaveStatus = 'idle' | 'saving' | 'saved' | 'error';
type ViewMode = 'columns' | 'sequence';

// ─── Helpers ─────────────────────────────────────────────────────────────────

const cloneDefaults = (): DraftState => ({
  phases:  JSON.parse(JSON.stringify(FLOW_PHASES)),
  columns: JSON.parse(JSON.stringify(FLOW_COLUMNS)),
  globalStepOrder: [],
});

const newStep = (colId: string): FlowStep => ({
  id: `${colId}-step-${Date.now()}`,
  label: 'New Step',
  icon: 'ri-checkbox-circle-line',
  guidance: { intro: '', tips: [], doneWhen: '' },
});

// Themed accent for this editor — aliased to the real success tokens so it
// tracks the platform theme (incl. dark mode) rather than a hardcoded hex.
const ACCENT = 'var(--bs-success)';
const ACCENT_TEXT = 'var(--bs-success-text-emphasis)';
const ACCENT_LIGHT = 'var(--bs-success-bg-subtle)';
const accentAlpha = (a: number) => `rgba(var(--bs-success-rgb), ${a})`;

// ─── Component ───────────────────────────────────────────────────────────────

export const IsmsJourneyAdminPage: React.FC = () => {

  const { getTenantId, user } = useAuth();
  const tenantId = getTenantId();
  // isPlatformAdmin checks against email addresses — pass user.email, not userId
  const isAuthorised = tenantId === '001' || isPlatformAdmin(user?.email);
  const swal = useSwal();

  const queryClient = useQueryClient();
  const { data: apiConfig, isLoading } = useQuery({
    queryKey: ['ismsFlowConfig'],
    queryFn:  async () => {
      try {
        const res = await fetch(`${API_BASE_URL}/config/isms-journey`, { method: 'GET', headers: getAuthHeaders() });
        if (!res.ok) return undefined;
        const data = await res.json() as { exists: boolean; columns?: FlowColumn[]; phases?: FlowPhase[]; globalStepOrder?: string[] };
        return data.exists ? data : undefined;
      } catch { return undefined; }
    },
    staleTime: 0, retry: false,
  });

  const [draft, setDraft]               = useState<DraftState>(cloneDefaults);
  const [saveStatus, setSaveStatus]     = useState<SaveStatus>('idle');
  const [saveMsg, setSaveMsg]           = useState('');
  // Selected step for the detail panel (right side)
  const [selectedStep, setSelectedStep] = useState<{ colId: string; stepId: string } | null>(null);
  // Which column header is open (accordion)
  const [openCols, setOpenCols]         = useState<Set<string>>(new Set());
  // Which column is being renamed inline
  const [editingColId, setEditingColId] = useState<string | null>(null);
  // View mode toggle
  const [viewMode, setViewMode] = useState<ViewMode>('columns');
  // Column picker for sequence-view "Add step"
  const [seqAddColId, setSeqAddColId] = useState<string>('');

  const [showAddColModal, setShowAddColModal] = useState(false);
  const [newColName, setNewColName] = useState('');
  // Prerequisites search filter
  const [depSearch, setDepSearch] = useState('');

  // Multi-select for mass delete
  const [checkedIds, setCheckedIds] = useState<Set<string>>(new Set());

  const baselineDraftRef = useRef<DraftState>(cloneDefaults());

  useEffect(() => {
    if (apiConfig?.exists && apiConfig.columns?.length && apiConfig.phases?.length) {
      const savedColumns: FlowColumn[] = JSON.parse(JSON.stringify(apiConfig.columns));
      const savedIds = new Set(savedColumns.map(c => c.id));

      // Append any bundled columns that are missing from the saved config.
      const merged = [
        ...savedColumns,
        ...FLOW_COLUMNS.filter(c => !savedIds.has(c.id)),
      ];

      const serverDraft: DraftState = {
        phases:  JSON.parse(JSON.stringify(apiConfig.phases)),
        columns: merged,
        globalStepOrder: JSON.parse(JSON.stringify(apiConfig.globalStepOrder ?? [])),
      };

      baselineDraftRef.current = serverDraft;
      setDraft(serverDraft);
    }
  }, [apiConfig]);

  useEffect(() => { setDepSearch(''); }, [selectedStep?.stepId]);

  const isDirty = JSON.stringify(draft) !== JSON.stringify(baselineDraftRef.current);

  const handleSave = useCallback(async () => {
    setSaveStatus('saving'); setSaveMsg('');
    try {
      await api.put('/config/isms-journey', draft);
      queryClient.invalidateQueries({ queryKey: ['ismsFlowConfig'] });
      baselineDraftRef.current = JSON.parse(JSON.stringify(draft));
      setSaveStatus('saved');
      setSaveMsg('Saved — all tenants will see changes on next load.');
      setTimeout(() => setSaveStatus('idle'), 4000);
    } catch (err: unknown) {
      setSaveStatus('error');
      setSaveMsg((err as { message?: string })?.message ?? 'Save failed');
    }
  }, [draft, queryClient]);

  const handleReset = async () => {
    if (!(await swal.confirm('Discard all unsaved changes?'))) return;
    setCheckedIds(new Set());
    setDraft(JSON.parse(JSON.stringify(baselineDraftRef.current)));
    setSaveStatus('idle'); setSelectedStep(null);
  };
  // ── Sequence helpers ──────────────────────────────────────────────────────

  const getSequencedSteps = (): Array<{ step: FlowStep; col: FlowColumn; seqIdx: number }> => {
    const allSteps: Array<{ step: FlowStep; col: FlowColumn }> = [];
    draft.columns.forEach(col => col.steps.forEach(step => allSteps.push({ step, col })));
    if (draft.globalStepOrder.length === 0) return allSteps.map((x, i) => ({ ...x, seqIdx: i }));
    const byId = new Map(allSteps.map(x => [x.step.id, x]));
    const ordered: Array<{ step: FlowStep; col: FlowColumn }> = [];
    const seen = new Set<string>();
    for (const id of draft.globalStepOrder) { const x = byId.get(id); if (x) { ordered.push(x); seen.add(id); } }
    allSteps.forEach(x => { if (!seen.has(x.step.id)) ordered.push(x); });
    return ordered.map((x, i) => ({ ...x, seqIdx: i }));
  };

  const moveStepInSequence = (stepId: string, direction: 'up' | 'down') => {
    setDraft(d => {
      const allIds = d.columns.flatMap(c => c.steps.map(s => s.id));
      const activeSet = new Set(allIds);
      const base = d.globalStepOrder.length > 0 ? d.globalStepOrder : allIds;
      const order = base.filter(id => activeSet.has(id));
      allIds.forEach(id => { if (!order.includes(id)) order.push(id); });
      const idx = order.indexOf(stepId);
      if (idx < 0) return d;
      const toIdx = direction === 'up' ? idx - 1 : idx + 1;
      if (toIdx < 0 || toIdx >= order.length) return d;
      const [moved] = order.splice(idx, 1);
      order.splice(toIdx, 0, moved);
      return { ...d, globalStepOrder: order };
    });
  };

  // ── Mutation helpers ──────────────────────────────────────────────────────

  const updateColumn = (colId: string, patch: Partial<Omit<FlowColumn, 'steps' | 'id' | 'phaseIndex'>>) =>
    setDraft(d => ({ ...d, columns: d.columns.map(c => c.id === colId ? { ...c, ...patch } : c) }));

  const updateStep = (colId: string, stepId: string, patch: Partial<FlowStep>) =>
    setDraft(d => ({ ...d, columns: d.columns.map(c => c.id !== colId ? c : { ...c, steps: c.steps.map(s => s.id === stepId ? { ...s, ...patch } : s) }) }));

  const updateGuidance = (colId: string, stepId: string, field: keyof FlowStep['guidance'], value: string | string[]) =>
    setDraft(d => ({ ...d, columns: d.columns.map(c => c.id !== colId ? c : { ...c, steps: c.steps.map(s => s.id !== stepId ? s : { ...s, guidance: { ...s.guidance, [field]: value } }) }) }));

  const moveStep = (colId: string, stepId: string, dir: -1 | 1) =>
    setDraft(d => ({ ...d, columns: d.columns.map(c => {
      if (c.id !== colId) return c;
      const steps = [...c.steps];
      const idx = steps.findIndex(s => s.id === stepId);
      const target = idx + dir;
      if (idx < 0 || target < 0 || target >= steps.length) return c;
      [steps[idx], steps[target]] = [steps[target], steps[idx]];
      return { ...c, steps };
    }) }));

  const deleteStep = async (colId: string, stepId: string) => {
    const step = draft.columns.find(c => c.id === colId)?.steps.find(s => s.id === stepId);
    if (!(await swal.confirmDelete(step?.label))) return;
    setDraft(d => ({ ...d, columns: d.columns.map(c => c.id !== colId ? c : { ...c, steps: c.steps.filter(s => s.id !== stepId) }) }));
    if (selectedStep?.stepId === stepId) setSelectedStep(null);
  };

  const deleteColumn = async (colId: string) => {
    const col = draft.columns.find(c => c.id === colId);
    if (!col) return;
    if (!(await swal.confirmDelete(col.label))) return;
    setDraft(d => ({ ...d, columns: d.columns.filter(c => c.id !== colId), globalStepOrder: d.globalStepOrder.filter(id => !col.steps.some(s => s.id === id)) }));
    if (selectedStep && draft.columns.find(c => c.id === colId)?.steps.some(s => s.id === selectedStep.stepId)) setSelectedStep(null);
    if (seqAddColId === colId) setSeqAddColId('');
  };

  // ── Mass-delete helpers ──────────────────────────────────────────────────

  const toggleCheck = (stepId: string, e: React.MouseEvent) => {
    e.stopPropagation();
    setCheckedIds(prev => { const n = new Set(prev); n.has(stepId) ? n.delete(stepId) : n.add(stepId); return n; });
  };

  const toggleCheckAll = () => {
    const allIds = draft.columns.flatMap(c => c.steps.map(s => s.id));
    if (checkedIds.size === allIds.length) setCheckedIds(new Set());
    else setCheckedIds(new Set(allIds));
  };

  const massDeleteChecked = async () => {
    if (checkedIds.size === 0) return;
    if (!(await swal.confirm(`Delete ${checkedIds.size} selected step${checkedIds.size !== 1 ? 's' : ''}?`, 'This cannot be undone.'))) return;
    setDraft(d => ({
      ...d,
      columns: d.columns.map(c => ({ ...c, steps: c.steps.filter(s => !checkedIds.has(s.id)) })),
      globalStepOrder: d.globalStepOrder.filter(id => !checkedIds.has(id)),
    }));
    if (selectedStep && checkedIds.has(selectedStep.stepId)) setSelectedStep(null);
    setCheckedIds(new Set());
  };

  const addStep = (colId: string) => {
    const step = newStep(colId);
    setDraft(d => ({ ...d, columns: d.columns.map(c => c.id !== colId ? c : { ...c, steps: [...c.steps, step] }) }));
    setSelectedStep({ colId, stepId: step.id });
    setOpenCols(prev => new Set(prev).add(colId));
  };

  const openAddColModal = () => { setNewColName(''); setShowAddColModal(true); };

  const addColumn = (label: string) => {
    const id = `col-${Date.now()}`;
    const col: FlowColumn = { id, label: label.trim() || 'New Column', icon: 'ri-checkbox-blank-circle-line', phaseIndex: 0, steps: [] };
    setDraft(d => ({ ...d, columns: [...d.columns, col] }));
    setOpenCols(prev => new Set(prev).add(id));
    setSeqAddColId(id);
    setShowAddColModal(false);
  };

  const getStep = (colId: string, stepId: string) =>
    draft.columns.find(c => c.id === colId)?.steps.find(s => s.id === stepId);

  const updateTip = (colId: string, stepId: string, idx: number, val: string) => {
    const tips = [...(getStep(colId, stepId)?.guidance.tips ?? [])];
    tips[idx] = val; updateGuidance(colId, stepId, 'tips', tips);
  };
  const addTip = (colId: string, stepId: string) => {
    updateGuidance(colId, stepId, 'tips', [...(getStep(colId, stepId)?.guidance.tips ?? []), '']);
  };
  const deleteTip = (colId: string, stepId: string, idx: number) => {
    updateGuidance(colId, stepId, 'tips', (getStep(colId, stepId)?.guidance.tips ?? []).filter((_, i) => i !== idx));
  };
  const moveTip = (colId: string, stepId: string, idx: number, dir: -1 | 1) => {
    const tips = [...(getStep(colId, stepId)?.guidance.tips ?? [])];
    const target = idx + dir;
    if (target < 0 || target >= tips.length) return;
    [tips[idx], tips[target]] = [tips[target], tips[idx]];
    updateGuidance(colId, stepId, 'tips', tips);
  };

  // ── Access guard ─────────────────────────────────────────────────────────

  if (!isAuthorised) return (
    <div className="container-xxl flex-grow-1 container-p-y">
      <div className="np-banner np-tone-danger mt-4">
        <i className="ri-lock-line fs-3" />
        <div><strong>Access Denied</strong><p className="mb-0 mt-1">The Journey Editor is only available to the platform administrator.</p></div>
      </div>
    </div>
  );

  // ── Derive selected step data ─────────────────────────────────────────────

  const selStep = selectedStep ? getStep(selectedStep.colId, selectedStep.stepId) ?? null : null;
  const selCol  = selectedStep ? draft.columns.find(c => c.id === selectedStep.colId) ?? null : null;

  // ─────────────────────────────────────────────────────────────────────────

  return (
    <>
    <div className="container-xxl flex-grow-1 container-p-y np-page">

      {/* ── Top bar ── */}
      <PageHeader
        icon="ri-route-line"
        title="ISMS Journey Editor"
        subtitle="Edit steps, guidance and tips. Changes go live immediately after saving."
        actions={
          <>
            {saveStatus === 'saved' && (
              <span className="text-success small fw-semibold"><i className="ri-check-double-line me-1" />{saveMsg}</span>
            )}
            {saveStatus === 'error' && (
              <span className="text-danger small fw-semibold"><i className="ri-error-warning-line me-1" />{saveMsg}</span>
            )}
            <button
              className="np-btn np-btn-primary np-btn-sm"
              onClick={handleSave}
              disabled={saveStatus === 'saving' || !isDirty}
            >
              {saveStatus === 'saving'
                ? <><span className="spinner-border spinner-border-sm me-1" />Saving…</>
                : <><i className="ri-save-line me-1" />{isDirty ? 'Save changes' : 'No changes'}</>}
            </button>
          </>
        }
      />

      {isLoading && (
        <div className="d-flex align-items-center gap-2 np-sub mb-3">
          <span className="spinner-border spinner-border-sm" />Loading current config…
        </div>
      )}

      {/* ── Two-panel layout ── */}
      <div className="row g-3" style={{ minHeight: '70vh' }}>

        {/* LEFT: Step tree / sequence */}
        <div className={selectedStep ? 'col-lg-5' : 'col-12'}>

          {/* View mode toggle */}
          <div className="d-flex align-items-center gap-2 mb-2">
            <div className="np-pill-toggle-group">
              <button
                type="button"
                className={`np-pill-toggle np-tone-primary${viewMode === 'columns' ? ' active' : ''}`}
                onClick={() => setViewMode('columns')}
              >
                <i className="ri-layout-column-line me-1" />Column view
              </button>
              <button
                type="button"
                className={`np-pill-toggle np-tone-primary${viewMode === 'sequence' ? ' active' : ''}`}
                onClick={() => setViewMode('sequence')}
              >
                <i className="ri-list-ordered me-1" />Step sequence
              </button>
            </div>
            {viewMode === 'sequence' && (
              <span className="np-sub" style={{ fontSize: '0.75rem' }}>
                {getSequencedSteps().length} steps total — reorder is saved globally
              </span>
            )}
          </div>

          {/* Mass-select toolbar */}
          <div className="d-flex align-items-center gap-2 mb-3" style={{ minHeight: 30 }}>
            <div className="form-check mb-0">
              <input
                type="checkbox"
                className="form-check-input"
                id="checkAll"
                checked={checkedIds.size > 0 && checkedIds.size === draft.columns.flatMap(c => c.steps).length}
                ref={el => { if (el) el.indeterminate = checkedIds.size > 0 && checkedIds.size < draft.columns.flatMap(c => c.steps).length; }}
                onChange={toggleCheckAll}
              />
              <label className="form-check-label small np-sub" htmlFor="checkAll">Select all</label>
            </div>
            {checkedIds.size > 0 && (
              <>
                <span className="np-sub" style={{ fontSize: '0.8rem' }}>{checkedIds.size} selected</span>
                <button
                  type="button"
                  className="np-btn np-btn-danger np-btn-sm"
                  onClick={massDeleteChecked}
                ><i className="ri-delete-bin-6-line me-1" />Delete selected ({checkedIds.size})</button>
                <button
                  type="button"
                  className="np-btn np-btn-ghost np-btn-sm"
                  onClick={() => setCheckedIds(new Set())}
                >Clear</button>
              </>
            )}
          </div>

          {/* ── SEQUENCE VIEW ── */}
          {viewMode === 'sequence' && (() => {
            const seqSteps = getSequencedSteps();
            const firstColId = draft.columns[0]?.id ?? '';
            return (
              <div className="np-card">
                {seqSteps.length === 0 && (
                  <div className="p-4 np-sub text-center" style={{ fontSize: '0.85rem' }}>
                    <i className="ri-list-ordered me-1"></i>No steps yet — add a column below, then add steps.
                  </div>
                )}
                {seqSteps.map(({ step, col, seqIdx }) => {
                  const isSelected = selectedStep?.stepId === step.id;
                  return (
                    <div
                      key={step.id}
                      className="d-flex align-items-center gap-2 px-3 py-2"
                      style={{
                        borderBottom: '1px solid var(--np-border)',
                        cursor: 'pointer',
                        backgroundColor: isSelected ? accentAlpha(0.12) : 'transparent',
                        borderLeft: isSelected ? `3px solid ${ACCENT}` : '3px solid transparent',
                        transition: 'background-color 0.1s',
                      }}
                      onClick={() => setSelectedStep({ colId: col.id, stepId: step.id })}
                    >
                      <input
                        type="checkbox"
                        className="form-check-input flex-shrink-0 mt-0"
                        style={{ cursor: 'pointer' }}
                        checked={checkedIds.has(step.id)}
                        onClick={e => toggleCheck(step.id, e)}
                        onChange={() => {}}
                      />
                      {/* Sequence number badge */}
                      <span
                        className="flex-shrink-0 d-flex align-items-center justify-content-center rounded-circle text-white fw-bold"
                        style={{ width: 22, height: 22, minWidth: 22, backgroundColor: step.isMilestone ? (step.milestoneColour === 'danger' ? 'var(--bs-danger)' : 'var(--bs-primary)') : ACCENT, fontSize: '0.62rem' }}
                      >{seqIdx + 1}</span>

                      {/* Move up / down */}
                      <div className="d-flex flex-column flex-shrink-0">
                        <button
                          type="button"
                          className="np-icon-btn"
                          style={{ width: '1.375rem', height: '1.375rem' }}
                          disabled={seqIdx === 0}
                          title="Move up"
                          onClick={e => { e.stopPropagation(); moveStepInSequence(step.id, 'up'); }}
                        ><i className="ri-arrow-up-s-line" style={{ fontSize: '0.8rem' }} /></button>
                        <button
                          type="button"
                          className="np-icon-btn"
                          style={{ width: '1.375rem', height: '1.375rem' }}
                          disabled={seqIdx === seqSteps.length - 1}
                          title="Move down"
                          onClick={e => { e.stopPropagation(); moveStepInSequence(step.id, 'down'); }}
                        ><i className="ri-arrow-down-s-line" style={{ fontSize: '0.8rem' }} /></button>
                      </div>

                      <i className={`${step.icon} flex-shrink-0`} style={{ color: 'var(--bs-secondary-color, var(--bs-body-color))', fontSize: '0.85rem' }} />

                      <span className="flex-grow-1 text-truncate" style={{ fontSize: '0.8rem' }}>{step.label}</span>

                      <span
                        className="np-chip np-tone-neutral flex-shrink-0 text-truncate"
                        style={{ maxWidth: 120 }}
                        title={col.label}
                      >
                        <i className={`${col.icon}`} />{col.label}
                      </span>

                      {step.isMilestone && (
                        <span className={`np-chip np-tone-${step.milestoneColour === 'danger' ? 'danger' : 'primary'} flex-shrink-0`}>milestone</span>
                      )}

                      <button
                        type="button" className="np-icon-btn np-icon-btn-danger flex-shrink-0"
                        title="Delete step"
                        onClick={e => { e.stopPropagation(); deleteStep(col.id, step.id); }}
                      ><i className="ri-delete-bin-6-line" /></button>
                    </div>
                  );
                })}

                {/* Footer actions */}
                <div className="d-flex align-items-center gap-2 p-2" style={{ borderTop: seqSteps.length > 0 ? '1px solid var(--np-border)' : 'none', backgroundColor: 'var(--np-surface-2)' }}>
                  {draft.columns.length > 0 && (
                    <div className="input-group input-group-sm flex-grow-1" style={{ maxWidth: 360 }}>
                      <select
                        className="np-select"
                        style={{ fontSize: '0.78rem' }}
                        value={seqAddColId || firstColId}
                        onChange={e => setSeqAddColId(e.target.value)}
                      >
                        {draft.columns.map(c => (
                          <option key={c.id} value={c.id}>{c.label}</option>
                        ))}
                      </select>
                      <button
                        type="button"
                        className="np-btn np-btn-primary np-btn-sm"
                        style={{ whiteSpace: 'nowrap' }}
                        onClick={() => addStep(seqAddColId || firstColId)}
                      >
                        <i className="ri-add-line me-1" />Add step
                      </button>
                    </div>
                  )}
                  <button
                    type="button"
                    className="np-btn np-btn-ghost np-btn-sm"
                    style={{ whiteSpace: 'nowrap' }}
                    onClick={() => openAddColModal()}
                  >
                    <i className="ri-add-circle-line me-1" />Add column
                  </button>
                </div>
              </div>
            );
          })()}

          {/* ── COLUMN VIEW ── */}
          {viewMode === 'columns' && <div className="np-card overflow-hidden mb-3">
            {draft.columns.map(col => {
              const colOpen = openCols.has(col.id);
              return (
                <div key={col.id} style={{ borderBottom: '1px solid var(--np-border)' }}>
                  {/* Column toggle row */}
                  <div
                    className="d-flex align-items-center gap-2 px-3 py-2"
                    style={{ backgroundColor: ACCENT_LIGHT, cursor: editingColId === col.id ? 'default' : 'pointer', userSelect: 'none' }}
                    onClick={() => { if (editingColId !== col.id) setOpenCols(prev => { const n = new Set(prev); colOpen ? n.delete(col.id) : n.add(col.id); return n; }); }}
                  >
                    {/* Per-column select-all checkbox */}
                    {col.steps.length > 0 && (
                      <input
                        type="checkbox"
                        className="form-check-input mt-0 flex-shrink-0"
                        style={{ cursor: 'pointer' }}
                        checked={col.steps.length > 0 && col.steps.every(s => checkedIds.has(s.id))}
                        ref={el => { if (el) el.indeterminate = col.steps.some(s => checkedIds.has(s.id)) && !col.steps.every(s => checkedIds.has(s.id)); }}
                        onClick={e => {
                          e.stopPropagation();
                          const allChecked = col.steps.every(s => checkedIds.has(s.id));
                          setCheckedIds(prev => {
                            const n = new Set(prev);
                            col.steps.forEach(s => allChecked ? n.delete(s.id) : n.add(s.id));
                            return n;
                          });
                        }}
                        onChange={() => {}}
                      />
                    )}
                    <i className={`${col.icon}`} style={{ color: ACCENT_TEXT, fontSize: '0.9rem', width: 18 }} />

                    {editingColId === col.id ? (
                      <div className="d-flex align-items-center gap-2 flex-grow-1" onClick={e => e.stopPropagation()}>
                        <input
                          type="text"
                          className="np-input"
                          style={{ maxWidth: 220 }}
                          value={col.label}
                          autoFocus
                          placeholder="Column label"
                          onChange={e => updateColumn(col.id, { label: e.target.value })}
                          onKeyDown={e => { if (e.key === 'Enter' || e.key === 'Escape') setEditingColId(null); }}
                        />
                        <input
                          type="text"
                          className="np-input"
                          style={{ maxWidth: 160 }}
                          value={col.icon}
                          placeholder="ri-icon-name"
                          onChange={e => updateColumn(col.id, { icon: e.target.value })}
                          onKeyDown={e => { if (e.key === 'Enter' || e.key === 'Escape') setEditingColId(null); }}
                        />
                        <button
                          type="button" className="np-btn np-btn-primary np-btn-sm"
                          onClick={() => setEditingColId(null)}
                        >Done</button>
                      </div>
                    ) : (
                      <span className="flex-grow-1 fw-semibold" style={{ fontSize: '0.8rem' }}>{col.label}</span>
                    )}

                    {editingColId !== col.id && (
                      <button
                        type="button"
                        className="np-icon-btn"
                        style={{ width: '1.5rem', height: '1.5rem' }}
                        title="Rename column"
                        onClick={e => { e.stopPropagation(); setEditingColId(col.id); setOpenCols(prev => new Set(prev).add(col.id)); }}
                      ><i className="ri-pencil-line" style={{ fontSize: '0.8rem' }} /></button>
                    )}
                    {editingColId !== col.id && (
                      <button
                        type="button"
                        className="np-icon-btn np-icon-btn-danger"
                        style={{ width: '1.5rem', height: '1.5rem' }}
                        title="Delete column"
                        onClick={e => { e.stopPropagation(); deleteColumn(col.id); }}
                      ><i className="ri-delete-bin-6-line" style={{ fontSize: '0.8rem' }} /></button>
                    )}
                    {editingColId !== col.id && (
                      <>
                        <span className="np-chip np-tone-neutral">{col.steps.length}</span>
                        <i className={`ri-arrow-${colOpen ? 'up' : 'down'}-s-line`} style={{ fontSize: '0.8rem', color: 'var(--bs-secondary-color, var(--bs-body-color))' }} />
                      </>
                    )}
                  </div>

                  {colOpen && (
                    <div className="px-3 pb-2 pt-1" style={{ backgroundColor: 'var(--np-surface)' }}>
                      {col.steps.length === 0 && (
                        <p className="np-sub" style={{ fontSize: '0.75rem', paddingLeft: 26, margin: '6px 0' }}>No steps</p>
                      )}
                      {col.steps.map((step, stepIdx) => {
                        const isSelected = selectedStep?.stepId === step.id;
                        return (
                          <div
                            key={step.id}
                            className="d-flex align-items-center gap-2 rounded px-2 py-1 mb-1"
                            style={{
                              cursor: 'pointer',
                              backgroundColor: isSelected ? accentAlpha(0.15) : 'transparent',
                              border: isSelected ? `1px solid ${accentAlpha(0.4)}` : '1px solid transparent',
                              transition: 'all 0.12s',
                            }}
                            onClick={() => setSelectedStep({ colId: col.id, stepId: step.id })}
                          >
                            <input
                              type="checkbox"
                              className="form-check-input flex-shrink-0 mt-0"
                              style={{ cursor: 'pointer' }}
                              checked={checkedIds.has(step.id)}
                              onClick={e => toggleCheck(step.id, e)}
                              onChange={() => {}}
                            />
                            <div className="d-flex flex-column flex-shrink-0">
                              <button
                                type="button" className="np-icon-btn"
                                style={{ width: '1.375rem', height: '1.375rem' }}
                                disabled={stepIdx === 0}
                                onClick={e => { e.stopPropagation(); moveStep(col.id, step.id, -1); }}
                              ><i className="ri-arrow-up-s-fill" style={{ fontSize: '0.8rem' }} /></button>
                              <button
                                type="button" className="np-icon-btn"
                                style={{ width: '1.375rem', height: '1.375rem' }}
                                disabled={stepIdx === col.steps.length - 1}
                                onClick={e => { e.stopPropagation(); moveStep(col.id, step.id, 1); }}
                              ><i className="ri-arrow-down-s-fill" style={{ fontSize: '0.8rem' }} /></button>
                            </div>

                            <i className={`${step.icon} flex-shrink-0`} style={{ color: step.isMilestone ? (step.milestoneColour === 'danger' ? 'var(--bs-danger)' : 'var(--bs-primary)') : 'var(--bs-secondary-color, var(--bs-body-color))', fontSize: '0.85rem' }} />

                            <span className="flex-grow-1 text-truncate" style={{ fontSize: '0.78rem' }}>{step.label}</span>

                            {step.isMilestone && (
                              <span className={`np-chip np-tone-${step.milestoneColour === 'danger' ? 'danger' : 'primary'}`}>milestone</span>
                            )}

                            <button
                              type="button" className="np-icon-btn np-icon-btn-danger flex-shrink-0"
                              onClick={e => { e.stopPropagation(); deleteStep(col.id, step.id); }}
                            ><i className="ri-delete-bin-6-line" /></button>
                          </div>
                        );
                      })}

                      <button
                        type="button"
                        className="np-btn np-btn-ghost np-btn-sm mt-1"
                        onClick={() => addStep(col.id)}
                      >
                        <i className="ri-add-line me-1" />Add step
                      </button>
                    </div>
                  )}
                </div>
              );
            })}
            <div className="p-2">
              <button
                type="button"
                className="np-btn np-btn-ghost np-btn-sm w-100"
                onClick={() => openAddColModal()}
              >
                <i className="ri-add-circle-line me-1" />Add column
              </button>
            </div>
          </div>}
        </div>

        {/* RIGHT: Step detail editor */}
        {selectedStep && selStep && selCol && (
          <div className="col-lg-7">
            <div className="np-card h-100" style={{ position: 'sticky', top: 80 }}>
              {/* Panel header */}
              <div
                className="np-card-head"
                style={{ backgroundColor: ACCENT_LIGHT, borderBottom: `3px solid ${ACCENT}`, padding: '0.875rem 1.25rem' }}
              >
                <i className={selCol.icon} style={{ color: ACCENT_TEXT, fontSize: '1.1rem' }} />
                <div className="flex-grow-1">
                  <div className="np-sub" style={{ fontSize: '0.68rem', textTransform: 'uppercase', letterSpacing: '0.04em' }}>{selCol.label}</div>
                  <div className="fw-semibold" style={{ fontSize: '0.85rem' }}>{selStep.label}</div>
                </div>
                <button type="button" className="btn-close btn-sm" onClick={() => setSelectedStep(null)} />
              </div>

              <div className="np-card-body overflow-auto" style={{ maxHeight: 'calc(100vh - 280px)' }}>

                {/* ── Step basics ── */}
                <div className="mb-4">
                  <h6 className="text-uppercase fw-bold mb-3" style={{ fontSize: '0.7rem', letterSpacing: '0.06em', color: 'var(--bs-secondary-color, var(--bs-body-color))' }}>Step details</h6>
                  <div className="row g-3">
                    <div className="col-8 np-field mb-0">
                      <label>Label</label>
                      <input
                        type="text" className="np-input"
                        value={selStep.label}
                        onChange={e => updateStep(selectedStep.colId, selectedStep.stepId, { label: e.target.value })}
                      />
                    </div>
                    <div className="col-4 np-field mb-0">
                      <label>Icon</label>
                      <div className="input-group">
                        <span className="input-group-text"><i className={selStep.icon} /></span>
                        <input
                          type="text" className="np-input"
                          value={selStep.icon}
                          placeholder="ri-checkbox-circle-line"
                          onChange={e => updateStep(selectedStep.colId, selectedStep.stepId, { icon: e.target.value })}
                        />
                      </div>
                    </div>
                    <div className="col-6 np-field mb-0">
                      <label>Link path <span className="fw-normal np-sub">(optional)</span></label>
                      <input
                        type="text" className="np-input"
                        value={selStep.path ?? ''}
                        placeholder="/risks  or  https://nimbus.app.mercell.com/#/risks"
                        onChange={e => updateStep(selectedStep.colId, selectedStep.stepId, { path: e.target.value || undefined })}
                      />
                    </div>
                    <div className="col-6 np-field mb-0">
                      <label>Button label <span className="fw-normal np-sub">(optional)</span></label>
                      <input
                        type="text" className="np-input"
                        value={selStep.actionLabel ?? ''}
                        placeholder="Go to Risks"
                        onChange={e => updateStep(selectedStep.colId, selectedStep.stepId, { actionLabel: e.target.value || undefined })}
                      />
                    </div>
                    <div className="col-12">
                      <div className="d-flex align-items-center gap-3">
                        <div className="d-flex align-items-center gap-2">
                          <label className="np-switch mb-0">
                            <input
                              type="checkbox"
                              id={`ms-${selStep.id}`}
                              checked={!!selStep.isMilestone}
                              onChange={e => updateStep(selectedStep.colId, selectedStep.stepId, { isMilestone: e.target.checked || undefined })}
                            />
                            <span className="np-switch-track"></span>
                            <span className="np-switch-thumb"></span>
                          </label>
                          <label className="small mb-0" htmlFor={`ms-${selStep.id}`}>Milestone step</label>
                        </div>
                        {selStep.isMilestone && (
                          <div className="d-flex gap-2">
                            {(['primary', 'danger'] as const).map(colour => (
                              <button
                                key={colour}
                                type="button"
                                className={`np-btn np-btn-${colour} np-btn-sm ${selStep.milestoneColour === colour ? '' : 'opacity-25'}`}
                                style={{ minWidth: 60 }}
                                onClick={() => updateStep(selectedStep.colId, selectedStep.stepId, { milestoneColour: colour })}
                              >
                                {colour === 'primary' ? 'Blue' : 'Red'}
                              </button>
                            ))}
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                </div>

                <hr className="my-3" />

                {/* ── Guidance ── */}
                <div className="mb-4">
                  <h6 className="text-uppercase fw-bold mb-3" style={{ fontSize: '0.7rem', letterSpacing: '0.06em', color: 'var(--bs-secondary-color, var(--bs-body-color))' }}>Guidance panel content</h6>

                  <div className="np-field">
                    <label>Introduction</label>
                    <ReactQuill
                      key={`intro-${selStep.id}`}
                      theme="snow"
                      value={selStep.guidance?.intro ?? ''}
                      onChange={val => updateGuidance(selectedStep.colId, selectedStep.stepId, 'intro', val)}
                      modules={{ toolbar: [['bold', 'italic', 'underline'], [{ list: 'ordered' }, { list: 'bullet' }], ['link'], ['clean']] }}
                      style={{ backgroundColor: 'var(--np-surface)' }}
                    />
                  </div>

                  <div className="np-field">
                    <label>Action tips</label>
                    {selStep.guidance.tips.length === 0 && (
                      <p className="np-hint mb-2"><i className="ri-lightbulb-line me-1"></i>No tips yet — click "Add tip" below.</p>
                    )}
                    {selStep.guidance.tips.map((tip, tipIdx) => (
                      <div key={tipIdx} className="d-flex align-items-start gap-2 mb-2">
                        <span
                          className="flex-shrink-0 d-flex align-items-center justify-content-center rounded-circle text-white fw-bold"
                          style={{ width: 22, height: 22, minWidth: 22, backgroundColor: ACCENT, fontSize: '0.65rem', marginTop: 9 }}
                        >
                          {tipIdx + 1}
                        </span>
                        <textarea
                          className="np-textarea"
                          rows={2}
                          value={tip}
                          placeholder={`Tip ${tipIdx + 1}…`}
                          onChange={e => updateTip(selectedStep.colId, selectedStep.stepId, tipIdx, e.target.value)}
                          style={{ resize: 'vertical' }}
                        />
                        <div className="d-flex flex-column gap-1 flex-shrink-0">
                          <button type="button" className="np-icon-btn"
                            style={{ width: '1.5rem', height: '1.5rem' }} disabled={tipIdx === 0}
                            onClick={() => moveTip(selectedStep.colId, selectedStep.stepId, tipIdx, -1)}
                          ><i className="ri-arrow-up-s-line" style={{ fontSize: '0.8rem' }} /></button>
                          <button type="button" className="np-icon-btn"
                            style={{ width: '1.5rem', height: '1.5rem' }} disabled={tipIdx === selStep.guidance.tips.length - 1}
                            onClick={() => moveTip(selectedStep.colId, selectedStep.stepId, tipIdx, 1)}
                          ><i className="ri-arrow-down-s-line" style={{ fontSize: '0.8rem' }} /></button>
                          <button type="button" className="np-icon-btn np-icon-btn-danger"
                            style={{ width: '1.5rem', height: '1.5rem' }}
                            onClick={() => deleteTip(selectedStep.colId, selectedStep.stepId, tipIdx)}
                          ><i className="ri-delete-bin-6-line" style={{ fontSize: '0.8rem' }} /></button>
                        </div>
                      </div>
                    ))}
                    <button
                      type="button"
                      className="np-btn np-btn-ghost np-btn-sm mt-1"
                      onClick={() => addTip(selectedStep.colId, selectedStep.stepId)}
                    >
                      <i className="ri-add-line me-1" />Add tip
                    </button>
                  </div>

                  <div className="np-field mb-0">
                    <label>Done when…</label>
                    <ReactQuill
                      key={`done-${selStep.id}`}
                      theme="snow"
                      value={selStep.guidance?.doneWhen ?? ''}
                      onChange={val => updateGuidance(selectedStep.colId, selectedStep.stepId, 'doneWhen', val)}
                      modules={{ toolbar: [['bold', 'italic', 'underline'], [{ list: 'ordered' }, { list: 'bullet' }], ['link'], ['clean']] }}
                      style={{ backgroundColor: 'var(--np-surface)' }}
                    />
                  </div>
                </div>

                <hr className="my-3" />

                {/* ── Prerequisites ── */}
                <div className="mb-3">
                  <h6 className="text-uppercase fw-bold mb-1" style={{ fontSize: '0.7rem', letterSpacing: '0.06em', color: 'var(--bs-secondary-color, var(--bs-body-color))' }}>Prerequisites</h6>
                  <p className="np-sub mb-2" style={{ fontSize: '0.78rem' }}>Steps that should be completed before this one. Shown as a reminder in the guidance panel.</p>
                  <div className="input-group input-group-sm mb-2">
                    <span className="input-group-text" style={{ backgroundColor: 'transparent' }}>
                      <i className="ri-search-line" style={{ fontSize: '0.8rem', color: 'var(--bs-secondary-color, var(--bs-body-color))' }} />
                    </span>
                    <input
                      type="text"
                      className="np-input"
                      placeholder="Search steps…"
                      value={depSearch}
                      onChange={e => setDepSearch(e.target.value)}
                    />
                    {depSearch && (
                      <button className="np-btn np-btn-ghost" type="button" onClick={() => setDepSearch('')}>
                        <i className="ri-close-line" style={{ fontSize: '0.8rem' }} />
                      </button>
                    )}
                  </div>
                  <div style={{ maxHeight: 280, overflowY: 'auto', border: '1px solid var(--np-border)', borderRadius: 6, padding: '8px 12px' }}>
                    {(() => {
                      const q = depSearch.toLowerCase();
                      const filteredCols = draft.columns
                        .map(c => ({
                          ...c,
                          steps: c.steps.filter(s =>
                            s.id !== selectedStep.stepId &&
                            !s.isMilestone &&
                            (!q || s.label.toLowerCase().includes(q) || c.label.toLowerCase().includes(q))
                          ),
                        }))
                        .filter(c => c.steps.length > 0);
                      if (filteredCols.length === 0) {
                        return <p className="np-hint mb-0">No steps match "{depSearch}"</p>;
                      }
                      return filteredCols.map(col => (
                        <div key={col.id} className="mb-2">
                          <div className="fw-semibold np-sub mb-1" style={{ fontSize: '0.68rem', textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                            <i className={`${col.icon} me-1`} />{col.label}
                          </div>
                          {col.steps.map(s => {
                            const isChecked = (selStep.dependsOn ?? []).includes(s.id);
                            return (
                              <div key={s.id} className="form-check mb-1">
                                <input
                                  className="form-check-input"
                                  type="checkbox"
                                  id={`dep-${s.id}`}
                                  checked={isChecked}
                                  onChange={() => {
                                    const curr = selStep.dependsOn ?? [];
                                    updateStep(selectedStep.colId, selectedStep.stepId, {
                                      dependsOn: isChecked ? curr.filter(id => id !== s.id) : [...curr, s.id],
                                    });
                                  }}
                                />
                                <label className="form-check-label small" htmlFor={`dep-${s.id}`} style={{ cursor: 'pointer' }}>
                                  <i className={`${s.icon} me-1`} style={{ fontSize: '0.72rem', color: 'var(--bs-secondary-color, var(--bs-body-color))' }} />
                                  {s.label}
                                </label>
                              </div>
                            );
                          })}
                        </div>
                      ));
                    })()}
                  </div>
                  {(selStep.dependsOn?.length ?? 0) > 0 && (
                    <p className="np-hint mt-1 mb-0">
                      <i className="ri-links-line me-1" />{selStep.dependsOn!.length} prerequisite{selStep.dependsOn!.length !== 1 ? 's' : ''} selected
                    </p>
                  )}
                </div>

              </div>
            </div>
          </div>
        )}

        {/* Empty state when nothing selected */}
        {!selectedStep && (
          <div className="col-12 text-center py-5 np-sub">
            <i className="ri-cursor-line" style={{ fontSize: '2rem', opacity: 0.3 }} />
            <p className="mt-2 mb-0 small">Select a step from the left to edit its details</p>
          </div>
        )}
      </div>

      {/* ── Sticky save bar ── */}
      {isDirty && (
        <div
          className="np-banner np-tone-warning mt-4"
          style={{ alignItems: 'center', justifyContent: 'space-between', position: 'sticky', bottom: 16, boxShadow: 'var(--np-shadow-md)' }}
        >
          <span className="small fw-semibold d-flex align-items-center">
            <i className="ri-alert-line me-2" />Unsaved changes
          </span>
          <div className="d-flex gap-2">
            <button className="np-btn np-btn-ghost np-btn-sm" onClick={handleReset}>Discard</button>
            <button className="np-btn np-btn-primary np-btn-sm" onClick={handleSave} disabled={saveStatus === 'saving'}>
              {saveStatus === 'saving' ? <><span className="spinner-border spinner-border-sm me-1" />Saving…</> : <><i className="ri-save-line me-1" />Save & publish</>}
            </button>
          </div>
        </div>
      )}

    </div>

    {/* Add Column Modal */}
    {showAddColModal && (
      <NimbusModal centered>
        <NimbusModalContent
          title="New column"
          onClose={() => setShowAddColModal(false)}
          footer={
            <>
              <button className="np-btn np-btn-ghost" onClick={() => setShowAddColModal(false)}>Cancel</button>
              <button
                className="np-btn np-btn-primary"
                disabled={!newColName.trim()}
                onClick={() => addColumn(newColName)}
              >Create column</button>
            </>
          }
        >
          <div className="np-field mb-0">
            <label>Column name</label>
            <input
              autoFocus
              className="np-input"
              placeholder="e.g. Implementation"
              value={newColName}
              onChange={e => setNewColName(e.target.value)}
              onKeyDown={e => { if (e.key === 'Enter' && newColName.trim()) addColumn(newColName); }}
            />
          </div>
        </NimbusModalContent>
      </NimbusModal>
    )}

    </>
  );
};

export default IsmsJourneyAdminPage;
